import type { TFunction } from "i18next";
import type { Notification } from "@/domain";

type NotificationGroupKey = "today" | "yesterday" | "last_7_days" | "earlier";

interface NotificationGroup {
  key: NotificationGroupKey;
  label: string;
  notifications: Notification[];
}

const GROUP_KEYS: NotificationGroupKey[] = ["today", "yesterday", "last_7_days", "earlier"];

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

function groupKeyOf(createdAt: string, today: number, dayInMs: number): NotificationGroupKey {
  const day = startOfDay(new Date(createdAt));
  if (day >= today) return "today";
  if (day === today - dayInMs) return "yesterday";
  return day > today - 7 * dayInMs ? "last_7_days" : "earlier";
}

/** The scratch groups: Today, Yesterday, Last 7 days and, for the API's older rows, Earlier. */
export function groupNotificationsByTime(
  notifications: Notification[],
  t: TFunction,
): NotificationGroup[] {
  const dayInMs = 24 * 60 * 60 * 1000;
  const today = startOfDay(new Date());
  const buckets: Record<NotificationGroupKey, Notification[]> = {
    today: [],
    yesterday: [],
    last_7_days: [],
    earlier: [],
  };
  for (const notification of notifications) {
    buckets[groupKeyOf(notification.created_at, today, dayInMs)].push(notification);
  }
  return GROUP_KEYS.filter((key) => buckets[key].length > 0).map((key) => ({
    key,
    label: t(`notifications.${key}`),
    notifications: buckets[key],
  }));
}
